import { ProductId } from "@beable/roster-backend-client-v1";
import { addDays, isBefore, format } from "date-fns";

export const today: Date = new Date(new Date().toDateString());
export const recommendedDate: string = format(addDays(today, 42), 'MM/dd/yyyy');
export const minimumRecommendedDays = 28; // this is the minimum recommended days in future for test dates
export const minRecommendedDateString: string = format(addDays(today, minimumRecommendedDays), 'MM/dd/yyyy');
export const minRecommendedDateDate = new Date(minRecommendedDateString);

export const defaultTestDateValidation: TestDateValidation = {
  siteId: '',
  productId: '',
  customerId: '',
  academicYearId: '',
  courseCode: '',
  testDate: '',
}

export const defaultTestDate: TestDate = {
  siteId: 0,
  siteSubdomain: '',
  customerId: '',
  customerName: '',
  productId: '',
  productName: '',
  academicYearId: '',
  schoolYear: 0,
  courseCode: '',
  testDate: '',
}

export type TestDateValidation = {
  [ key: string ]: any;
  siteId: string;
  productId: string;
  customerId: string;
  academicYearId: string;
  courseCode: string;
  testDate: string;
}

export interface TestDate {
  id?: string;
  testDate: Date | string;
  siteId: number;
  siteSubdomain: string;
  productId: string;
  productName: string;
  customerId: string;
  customerName: string;
  academicYearId: string;
  schoolYear: number;
  courseCode: string;
}

export declare class TestDateFilter {
  testDate?: Date | string;
  siteId?: number;
  productId?: string;
  customerId?: string;
  academicYearId?: string;
  courseCode?: string;
}

export function validateTestDate(date: string, savedTestDate?: string): { dateError: string } {
  let dateError = '';
  
  if(isBefore(new Date(date), today)) {
    dateError = 'Test date cannot be a past date.';
  } else if(isBefore(new Date(getUtcDateString(date)), minRecommendedDateDate)) {
    if (!savedTestDate) {
      dateError = `The test date must be at least ${minimumRecommendedDays} days away.`;
    } else {
      if (isBefore(new Date(date), new Date(savedTestDate))) {
        let messageDate = getUtcDateString(minRecommendedDateDate);
        if (isBefore(new Date(savedTestDate), minRecommendedDateDate)) {
          messageDate = format(new Date(savedTestDate).valueOf() + new Date(savedTestDate).getTimezoneOffset() * 60 * 1000, 'MM/dd/yyyy');
        }
        dateError = `The test date cannot be earlier than ${messageDate}.`;
      }
    }
  }

  return { dateError };
}

export async function validateForm(
  customerId: string,
  siteId: number,
  productId: string,
  academicYearId: string,
  courseCode: string,
  testDate: string,
  savedTestDate?: string,
): Promise<{ validation: TestDateValidation; isInvalid: boolean }> {

  const validation: TestDateValidation = Object.assign(
    {},
    defaultTestDateValidation,
  );
  let isInvalid = false;

  if (!siteId) {
    isInvalid = true;
    validation.siteId = `Site should not be empty`;
  }

  if (!customerId) {
    isInvalid = true;
    validation.customerId = `Customer should not be empty`;
  }

  if (!productId) {
    isInvalid = true;
    validation.productId = `Product should not be empty`;
  }
  if (!academicYearId) {
    isInvalid = true;
    validation.academicYearId = `Academic year should not be empty`;
  }
  if (!courseCode) {
    isInvalid = true;
    validation.courseCode = `Course code should not be empty`;
  }

  if (productId == ProductId.Exampal.valueOf()) {

    let error = '';
    if (testDate === undefined) {
      error = 'Invalid Date';
    } else {
      const { dateError } = validateTestDate(testDate, savedTestDate);
      error = dateError ?? '';
    }

    if (error) {
      validation.testDate = error;
      isInvalid = true;
    }
  }

  return { validation, isInvalid };
}

export const getWarningMessage = (newDate: string, previousDate?: string): string | undefined => {
    let warning: string | undefined;
    const selectedDate = new Date(newDate);

    if (isBefore(selectedDate, today)) {
        warning = `Test date cannot be a past date.`;
    } else {
        const { dateError } = validateTestDate(newDate, previousDate);
        warning = dateError;
    }

    return warning;
};

export const getUtcDateString = (date: Date | string, dateFormat = 'MM/dd/yyyy') => {
  const year = new Date(date).getUTCFullYear();
  const month = new Date(date).getUTCMonth();
  const day = new Date(date).getUTCDate();
  const dateString = format(new Date(year, month, day), dateFormat);
  return dateString;
};
